import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mic, MicOff, PhoneOff, Video, VideoOff } from 'lucide-react';
import {
  RELIABLE_VIDEO_PUBLISHER_OPTIONS,
  applyPublisherNetworkProfile,
} from '../../utils/vonagePublisherQuality';
import { createLiveKitSession, isMeditalkCall } from '../video-call/livekitSession';

const PHARMACY_TIME_ZONE = process.env.REACT_APP_PHARMACY_TIME_ZONE || 'Canada/Pacific';

const parseVonageSessionId = (sessionId) => {
  if (!sessionId || typeof sessionId !== 'string') return { apiKey: null, sessionId };
  const normalized = String(sessionId).trim().replace(/\s+/g, '');
  const parts = normalized.split('-');
  if (parts[0] && parts[0].startsWith('2_MX')) {
    return { apiKey: parts[0].substring(4), sessionId: normalized };
  }
  return { apiKey: null, sessionId: normalized };
};

const normalizeToken = (token) => (token == null ? '' : String(token).trim().replace(/\s+/g, ''));

const getPharmacyCurrentDateTimeParts = () => {
  const currentTime = new Date();
  const options = {
    timeZone: PHARMACY_TIME_ZONE,
    hour12: true,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  const full = currentTime.toLocaleString('en-US', options);
  const lastComma = full.lastIndexOf(', ');

  if (lastComma === -1) {
    return { datePart: full, timePart: '' };
  }

  return {
    datePart: full.slice(0, lastComma),
    timePart: full.slice(lastComma + 2),
  };
};

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return 'D';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
};

const DriverAvatar = ({ name, image, size = 'large', tone = 'dark' }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const dimensions = size === 'small' ? 'h-10 w-10 text-sm' : 'h-24 w-24 text-3xl';
  const frameClass = tone === 'light'
    ? 'border-slate-200 bg-slate-100 text-slate-700 shadow-sm'
    : 'border-white/15 bg-white/10 text-white shadow-[0_18px_38px_rgba(0,0,0,0.32)]';
  const imageFrameClass = tone === 'light'
    ? 'border-slate-200 shadow-sm'
    : 'border-white/20 shadow-[0_18px_38px_rgba(0,0,0,0.32)]';

  if (image && !imageFailed) {
    return (
      <img
        src={image}
        alt=""
        onError={() => setImageFailed(true)}
        className={`${dimensions} rounded-full border object-cover ${imageFrameClass}`}
      />
    );
  }

  return (
    <div className={`${dimensions} flex items-center justify-center rounded-full border font-semibold ${frameClass}`}>
      {getInitials(name)}
    </div>
  );
};

const ControlButton = ({
  onClick,
  active = true,
  variant = 'neutral',
  title,
  children,
}) => {
  const className =
    variant === 'danger'
      ? 'bg-red-600 text-white hover:bg-red-700 shadow-[0_14px_28px_rgba(220,38,38,0.34)]'
      : active
        ? 'bg-white/10 text-zinc-100 hover:bg-white/20'
        : 'bg-red-500/20 text-red-200 hover:bg-red-500/30';

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex h-11 w-11 items-center justify-center rounded-full transition ${className}`}
    >
      {children}
    </button>
  );
};

const PharmacistDriverVideoRoom = ({
  isOpen,
  callData,
  callState,
  onCallerReady,
  onClose,
}) => {
  const publisherRef = useRef(null);
  const subscriberRef = useRef(null);
  const sessionRef = useRef(null);
  const publisherInstanceRef = useRef(null);
  const livekitSessionRef = useRef(null);
  const callerReadyNotifiedRef = useRef(null);
  const isMicOnRef = useRef(true);
  const isVideoOnRef = useRef(true);
  const [connectionNotice, setConnectionNotice] = useState(null);
  const [remoteJoined, setRemoteJoined] = useState(false);
  const [isRemotePortrait, setIsRemotePortrait] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [callDateTime, setCallDateTime] = useState(() => getPharmacyCurrentDateTimeParts());

  const driverName = callData?.driverName || callData?.driver?.name || 'Assistant';
  const driverPhoto = callData?.driverPhoto || callData?.driver?.driver_photo || callData?.driver?.photo || '';
  const pharmacistName = callData?.pharmacistName || callData?.pharmacist?.name || 'Pharmacist';

  const parsedSession = useMemo(
    () => parseVonageSessionId(callData?.vonage_session_id),
    [callData?.vonage_session_id]
  );
  const apiKey = useMemo(
    () => (callData?.application_id ? String(callData.application_id).trim().replace(/\s+/g, '') : parsedSession.apiKey),
    [callData?.application_id, parsedSession.apiKey]
  );
  const token = useMemo(() => normalizeToken(callData?.vonage_token), [callData?.vonage_token]);
  // Which SDK this call belongs to. Pinned by the backend on the call row, so
  // both participants always agree. MediTalk reuses the vonage_* slots for the
  // LiveKit room name and JWT, and sends no application_id.
  const isMeditalk = isMeditalkCall(callData);
  const livekitUrl = callData?.livekit_url || '';
  const canConnect = Boolean(
    isOpen
    && parsedSession.sessionId
    && token
    && (isMeditalk ? livekitUrl : apiKey)
  );

  // The only two fields the LiveKit adapter reads off the call row. Memoised so
  // the connect effect below can depend on a stable value: `callData` itself is
  // replaced by the manager on EVERY realtime status message (including
  // `accepted`), and depending on that object tore the room down and rebuilt it
  // at the exact instant the assistant answered — the caller then reconnected
  // with the same LiveKit identity, threw, and reported "Unable to connect the
  // video call." on a perfectly good room.
  const livekitCallData = useMemo(
    () => ({ video_provider: callData?.video_provider, livekit_url: callData?.livekit_url }),
    [callData?.video_provider, callData?.livekit_url]
  );
  // Read inside the effect without making the effect depend on it: the id is
  // constant for the lifetime of a call, and `callerReadyNotifiedRef` already
  // makes the notify idempotent per call.
  const callIdRef = useRef(callData?.callId);
  callIdRef.current = callData?.callId;

  const clearPublisherContainer = () => {
    if (publisherRef.current) {
      publisherRef.current.innerHTML = '';
    }
  };

  const getSubscriberVideoElement = useCallback(
    () => subscriberRef.current?.querySelector('video') ?? null,
    []
  );

  const updateRemotePortraitState = useCallback(() => {
    const videoElement = getSubscriberVideoElement();
    if (!videoElement) return;

    const { videoWidth, videoHeight } = videoElement;
    if (!videoWidth || !videoHeight) return;

    setIsRemotePortrait(videoHeight / videoWidth >= 1.15);
  }, [getSubscriberVideoElement]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.classList.add('pharmacist-driver-video-call-active');
    const tick = () => setCallDateTime(getPharmacyCurrentDateTimeParts());
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => {
      document.body.classList.remove('pharmacist-driver-video-call-active');
      window.clearInterval(intervalId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsRemotePortrait(false);
      return undefined;
    }

    let currentVideo = null;
    const bindVideo = () => {
      const nextVideo = getSubscriberVideoElement();
      if (!nextVideo || nextVideo === currentVideo) return;

      if (currentVideo) {
        currentVideo.removeEventListener('loadedmetadata', updateRemotePortraitState);
        currentVideo.removeEventListener('resize', updateRemotePortraitState);
      }

      currentVideo = nextVideo;
      currentVideo.addEventListener('loadedmetadata', updateRemotePortraitState);
      currentVideo.addEventListener('resize', updateRemotePortraitState);
      updateRemotePortraitState();
    };

    bindVideo();
    const observer = new MutationObserver(bindVideo);
    if (subscriberRef.current) {
      observer.observe(subscriberRef.current, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
      if (currentVideo) {
        currentVideo.removeEventListener('loadedmetadata', updateRemotePortraitState);
        currentVideo.removeEventListener('resize', updateRemotePortraitState);
      }
    };
  }, [getSubscriberVideoElement, isOpen, updateRemotePortraitState]);

  useEffect(() => {
    isMicOnRef.current = isMicOn;
    const livekitSession = livekitSessionRef.current;
    if (livekitSession) {
      livekitSession.setMicrophoneEnabled(isMicOn).catch(() => {
        setConnectionNotice(`Unable to ${isMicOn ? 'unmute' : 'mute'} the microphone right now.`);
      });
      return;
    }

    const publisher = publisherInstanceRef.current;
    if (!publisher) return;
    try {
      publisher.publishAudio(isMicOn);
    } catch (error) {
      setConnectionNotice(`Unable to ${isMicOn ? 'unmute' : 'mute'} the microphone right now.`);
    }
  }, [isMicOn]);

  useEffect(() => {
    isVideoOnRef.current = isVideoOn;
    const livekitSession = livekitSessionRef.current;
    if (livekitSession) {
      livekitSession.setCameraEnabled(isVideoOn).catch(() => {
        setConnectionNotice(`Unable to turn ${isVideoOn ? 'on' : 'off'} the camera right now.`);
      });
      return;
    }

    const publisher = publisherInstanceRef.current;
    if (!publisher) return;
    try {
      publisher.publishVideo(isVideoOn);
    } catch (error) {
      setConnectionNotice(`Unable to turn ${isVideoOn ? 'on' : 'off'} the camera right now.`);
    }
  }, [isVideoOn]);

  // ---------------------------------------------------------------- MediTalk
  // LiveKit path. Runs only for MediTalk calls; the Vonage effect below bails
  // out for them, so exactly one of the two ever owns the session.
  useEffect(() => {
    if (!canConnect || !isMeditalk) return undefined;

    const session = createLiveKitSession(livekitCallData);
    if (!session) return undefined;

    let cancelled = false;
    const subscriberContainer = subscriberRef.current;
    const publisherContainer = publisherRef.current;
    livekitSessionRef.current = session;

    session.on('streamCreated', ({ stream }) => {
      if (cancelled || !subscriberContainer) return;
      setRemoteJoined(true);
      setConnectionNotice(null);
      session.attach(stream.track, subscriberContainer);
    });

    // NOT streamDestroyed / participantDisconnected: LiveKit fires those during
    // ordinary reconnects. `remoteLeft` is the adapter's post-grace-period one.
    session.on('remoteLeft', () => {
      if (cancelled) return;
      setRemoteJoined(false);
      setIsRemotePortrait(false);
    });

    session.on('reconnecting', () => {
      if (!cancelled) setConnectionNotice('Reconnecting...');
    });
    session.on('reconnected', () => {
      if (!cancelled) setConnectionNotice(null);
    });

    // Auto-answered calls have had no user gesture, so the browser can refuse
    // to play the remote audio. One click anywhere releases it.
    const unblockAudio = () => {
      session.startAudio().catch(() => {});
    };
    session.on('autoplayBlocked', () => {
      if (cancelled) return;
      setConnectionNotice('Click anywhere to enable call audio.');
      document.addEventListener('click', unblockAudio, { once: true });
    });

    // A page refresh does not run React cleanup, and LiveKit will hold the
    // participant slot until its own timeout. Leave the room explicitly so the
    // pharmacist's line frees immediately, as the Vonage SDK used to do.
    const leaveRoom = () => {
      try {
        session.disconnect();
      } catch (error) { }
    };
    window.addEventListener('beforeunload', leaveRoom);
    window.addEventListener('pagehide', leaveRoom);

    (async () => {
      try {
        await session.connect(token);
        if (cancelled) {
          session.disconnect();
          return;
        }

        await session.publish({
          role: 'pharmacist',
          publishAudio: isMicOnRef.current,
          publishVideo: isVideoOnRef.current,
        });
        if (cancelled) return;

        session.attachLocalPreview(publisherContainer);

        const callId = callIdRef.current;
        if (callId && callerReadyNotifiedRef.current !== callId) {
          callerReadyNotifiedRef.current = callId;
          onCallerReady?.(callId);
        }
      } catch (error) {
        if (!cancelled) {
          setConnectionNotice('Unable to connect the video call.');
        }
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('beforeunload', leaveRoom);
      window.removeEventListener('pagehide', leaveRoom);
      document.removeEventListener('click', unblockAudio);
      setRemoteJoined(false);
      setIsRemotePortrait(false);
      leaveRoom();
      livekitSessionRef.current = null;
      if (publisherContainer) publisherContainer.innerHTML = '';
      if (subscriberContainer) subscriberContainer.innerHTML = '';
    };
    // Primitives + one memoised object only, mirroring the Vonage effect below.
    // A status message that carries no new credentials must NOT re-run this.
  }, [canConnect, isMeditalk, livekitCallData, onCallerReady, token]);

  // ------------------------------------------------------------------ Vonage
  useEffect(() => {
    if (!canConnect || isMeditalk) return undefined;

    let cancelled = false;
    let session = null;
    let preparedPublisher = null;
    let canPublishToSession = true;
    const subscriberContainer = subscriberRef.current;

    const createPublisher = (OT, { publishAudio = true, publishVideo = true } = {}) => (
      new Promise((resolve, reject) => {
        if (!publisherRef.current) {
          resolve(null);
          return;
        }

        clearPublisherContainer();
        const publisher = OT.initPublisher(publisherRef.current, {
          insertMode: 'append',
          width: '100%',
          height: '100%',
          fitMode: 'cover',
          showControls: false,
          ...RELIABLE_VIDEO_PUBLISHER_OPTIONS,
          publishAudio,
          publishVideo,
        }, (error) => {
          if (error) {
            try {
              publisher?.destroy?.();
            } catch (destroyError) {}
            clearPublisherContainer();
            reject(error);
            return;
          }

          preparedPublisher = publisher;
          applyPublisherNetworkProfile(publisher);
          if (cancelled || !canPublishToSession) {
            try {
              publisher.destroy();
            } catch (destroyError) {}
            preparedPublisher = null;
            resolve(null);
            return;
          }

          resolve(publisher);
        });
      })
    );

    try {
      const OT = require('@vonage/client-sdk-video');
      session = OT.initSession(apiKey, parsedSession.sessionId);
      sessionRef.current = session;

      session.on('streamCreated', (event) => {
        if (cancelled || !subscriberContainer) return;
        setRemoteJoined(true);
        setConnectionNotice(null);
        try {
          session.subscribe(event.stream, subscriberContainer, {
            insertMode: 'append',
            width: '100%',
            height: '100%',
            fitMode: 'cover',
            showControls: false,
          });
        } catch (error) {
          setConnectionNotice('Video connection is having trouble.');
        }
      });

      session.on('streamDestroyed', () => {
        if (!cancelled) {
          setRemoteJoined(false);
          setIsRemotePortrait(false);
        }
      });

      const preparedPublisherPromise = createPublisher(OT)
        .catch((firstError) => {
          if (cancelled) throw firstError;
          setConnectionNotice('Camera or microphone access failed. Retrying video connection.');
          return createPublisher(OT);
        });

      preparedPublisherPromise.catch(() => {});

      session.connect(token, (error) => {
        if (cancelled) return;
        if (error) {
          canPublishToSession = false;
          if (preparedPublisher) {
            try {
              preparedPublisher.destroy();
            } catch (destroyError) {}
            preparedPublisher = null;
          }
          setConnectionNotice('Unable to connect the video call.');
          return;
        }
        preparedPublisherPromise
          .then((publisher) => {
            if (!publisher || cancelled || !canPublishToSession) return;
            session.publish(publisher, (publishError) => {
              if (publishError && !cancelled) {
                setConnectionNotice('Connected, but your camera could not be published.');
                return;
              }
              publisherInstanceRef.current = publisher;
              preparedPublisher = publisher;
              const callId = callData?.callId;
              if (callId && callerReadyNotifiedRef.current !== callId) {
                callerReadyNotifiedRef.current = callId;
                onCallerReady?.(callId);
              }
            });
          })
          .catch(() => {
            if (!cancelled) {
              setConnectionNotice('Camera or microphone access failed.');
            }
          });
      });
    } catch (error) {
      if (!cancelled) {
        setConnectionNotice('Unable to start the video call.');
      }
    }

    return () => {
      cancelled = true;
      setRemoteJoined(false);
      setIsRemotePortrait(false);
      try {
        if (session && publisherInstanceRef.current) {
          session.unpublish(publisherInstanceRef.current);
        }
      } catch (error) {}
      try {
        if (preparedPublisher && preparedPublisher !== publisherInstanceRef.current) {
          preparedPublisher.destroy();
        }
      } catch (error) {}
      try {
        session?.disconnect?.();
      } catch (error) {}
      publisherInstanceRef.current = null;
      sessionRef.current = null;
      clearPublisherContainer();
      if (subscriberContainer) subscriberContainer.innerHTML = '';
    };
  }, [apiKey, callData?.callId, canConnect, isMeditalk, onCallerReady, parsedSession.sessionId, token]);

  if (!isOpen) return null;

  const statusText =
    callState === 'accepted'
      ? (remoteJoined ? 'Connected' : 'Waiting for assistant video...')
      : callState === 'requesting'
        ? 'Calling...'
        : callState === 'declined'
          ? 'Declined'
          : callState === 'expired'
            ? 'Missed'
            : 'Call ended';

  return (
    <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-[radial-gradient(circle_at_top,rgba(59,130,246,0.24),transparent_28%),linear-gradient(135deg,#020617_0%,#0f172a_42%,#13233d_100%)] text-white">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_80%_20%,rgba(168,85,247,0.12),transparent_24%),radial-gradient(circle_at_15%_80%,rgba(34,197,94,0.09),transparent_20%)]" />

      <div className="relative flex h-full w-full justify-center md:block">
        <main className="m-0 h-full w-full max-w-md flex-none p-0 md:max-w-none md:flex-1 md:p-3">
          <div className="flex h-full w-full flex-col gap-4">
            <div className={`relative min-w-0 w-full flex-1 ${isRemotePortrait ? 'flex items-center justify-center' : ''}`}>
              <div className={`relative h-full min-h-0 overflow-hidden border border-white/10 bg-black shadow-[0_28px_90px_rgba(2,6,23,0.5)] transition-all duration-300 ease-out md:min-h-[420px] md:rounded-xl ${
                isRemotePortrait ? 'h-[min(100%,calc(100vh-2.5rem))] w-[min(100%,440px)] max-w-[440px] aspect-[9/16] rounded-xl' : ''
              }`}>
                <div ref={subscriberRef} className="absolute inset-0" />

                {connectionNotice && (
                  <div className="absolute left-3 right-3 top-3 z-20 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 shadow-lg md:left-5 md:right-auto md:top-5 md:max-w-md">
                    {connectionNotice}
                  </div>
                )}

                {!remoteJoined && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-slate-950/92 text-center">
                    <DriverAvatar name={driverName} image={driverPhoto} />
                    <div>
                      <p className="text-2xl font-semibold text-white">{driverName}</p>
                      <p className="mt-2 text-sm font-medium text-slate-300">{statusText}</p>
                    </div>
                  </div>
                )}

                <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-slate-950/70 via-slate-950/20 to-transparent" />

                <div
                  className={`absolute top-3 z-20 flex flex-col gap-2 md:top-5 ${
                    isRemotePortrait
                      ? 'left-1/2 w-[min(92%,460px)] -translate-x-1/2'
                      : 'left-3 right-3 md:left-auto md:right-5'
                  }`}
                >
                  <div
                    className={`rounded-[22px] border border-black/80 bg-slate-900/95 px-3 py-2.5 text-white shadow-[0_10px_36px_rgba(0,0,0,0.5),0_4px_14px_rgba(0,0,0,0.45),0_1px_3px_rgba(0,0,0,0.35)] backdrop-blur-md md:px-4 md:py-3 ${isRemotePortrait ? 'w-full' : ''}`}
                    style={{ background: '#161515' }}
                  >
                    <p className="truncate text-sm font-semibold">{driverName} with {pharmacistName}</p>
                    <div className={`mt-2 flex items-center justify-start gap-1.5 text-[11px] md:gap-2 ${isRemotePortrait ? 'flex-nowrap overflow-hidden' : 'flex-wrap'}`}>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-green-600 px-2.5 py-1 font-bold uppercase tracking-[0.14em] text-white shadow-sm">
                        <span className="h-2 w-2 shrink-0 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.85)]" aria-hidden />
                        {callState === 'accepted' ? 'Live' : 'Calling'}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-yellow-300/55 bg-yellow-500/25 px-2.5 py-1 font-semibold text-yellow-50">
                        {callDateTime.datePart}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full border border-sky-300/55 bg-sky-500/25 px-2.5 py-1 font-semibold text-sky-50">
                        {callDateTime.timePart}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={`absolute overflow-hidden rounded-[22px] border border-white/20 bg-slate-900 shadow-2xl ${
                  isRemotePortrait
                    ? 'bottom-5 right-5 h-32 w-24'
                    : 'bottom-24 right-4 h-32 w-24 md:bottom-5 md:right-5 md:h-40 md:w-28 lg:h-52 lg:w-40'
                }`}>
                  <div ref={publisherRef} className="h-full w-full" />
                  {!isVideoOn && (
                    <div className="absolute inset-0 bg-slate-950/88" />
                  )}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-3 pb-2 pt-8">
                    <span className="inline-flex max-w-full items-center rounded-full bg-black/60 px-2.5 py-1 text-xs font-semibold text-white shadow-sm backdrop-blur">
                      YOU
                    </span>
                  </div>
                </div>
              </div>

              <div
                className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center gap-1 px-4 py-2"
                style={{
                  backgroundColor: 'rgba(39, 39, 42, 0.95)',
                  borderColor: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: '9999px',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <ControlButton onClick={() => setIsMicOn((previous) => !previous)} active={isMicOn} title={isMicOn ? 'Mute microphone' : 'Unmute microphone'}>
                  {isMicOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                </ControlButton>
                <ControlButton onClick={() => setIsVideoOn((previous) => !previous)} active={isVideoOn} title={isVideoOn ? 'Turn camera off' : 'Turn camera on'}>
                  {isVideoOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                </ControlButton>
                <ControlButton onClick={onClose} variant="danger" title="End call">
                  <PhoneOff className="h-5 w-5" />
                </ControlButton>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PharmacistDriverVideoRoom;
